/**
 * The single, canonical catalog of referenceable campaign attributes.
 *
 * Every attribute an author can drop into report prose OR an AI prompt has exactly ONE spelling:
 * `{{group.key}}` (double-curly, lowercase, dot-namespaced, charset `[a-z0-9_.]`). This module is the
 * one place that defines which attributes exist, how each resolves against an {@link AttributeContext},
 * and how the picker groups/labels them. Both substitution seams compose the shared resolver in
 * `resolve.ts`, so a `{{…}}` token means the same thing everywhere (screen / PDF / Word / AI grounding).
 *
 * Why `{{group.key}}` is additive and parity-safe:
 *  - The report seam (`applyTokens`) matches only `[...]`; the prompt seam (`fillTemplate`) matches
 *    `{word}` where `\w` excludes `.`, so it never sees `{{customer.name}}`; a fenced-JSON `{"…"}`
 *    starts with `{"`. The three regexes are mutually disjoint.
 *  - Legacy tokens keep resolving through their own engines untouched. A few static entries carry a
 *    `legacyAlias` (the equivalent `[Bracketed]`) purely as an author hint — the alias is NOT how they
 *    resolve, it just tells the author the two spellings are interchangeable.
 *  - Frontier's seeded text contains zero `{{…}}`, so the resolver is a no-op over it → byte-identical.
 *
 * Attributes come in two forms: EXACT keys (`customer.name`, `outcome.list`) and FAMILIES — a `pattern`
 * that matches many concrete instance keys (`capability.<key>.name`, `answer.<stepKey>.<questionKey>`).
 * The picker (`useAttributeOptions`) expands families into concrete options from live campaign rows;
 * the resolver matches a concrete key back to its family def to resolve it.
 */

import type { AttributeContext } from './context';

export type AttributeGroup =
  | 'customer'
  | 'salesforce'
  | 'outcome'
  | 'persona'
  | 'role'
  | 'capability'
  | 'question'
  | 'answer'
  | 'gap'
  | 'deepdive'
  | 'webex';

/** Where an attribute's value comes from — drives the picker's availability filter. */
export type AttributeSource = 'static' | 'sf' | 'reference' | 'dynamic';

/**
 * The authoring scope an attribute belongs to.
 *  - `campaign`: authored once for the whole campaign and referenceable everywhere (enablement AND
 *    per-delivery text) — outcomes, personas, team roles, capabilities, the question pool, the roster.
 *  - `delivery`: only exists once a delivery is running against a real account — the customer/opportunity
 *    basics, Salesforce fields, captured discovery/deep-dive answers, open gaps, and deep-dive findings.
 *
 * The picker hides `delivery`-scoped groups in enablement fields (where they are guaranteed never to
 * resolve), and offers everything in per-delivery fields. This is an authoring-UI affordance only: the
 * runtime resolver still leaves any unresolved token verbatim regardless of where it was authored.
 */
export type AttributeScope = 'campaign' | 'delivery';

/** The authoring scope of each attribute group. See {@link AttributeScope}. */
export const GROUP_SCOPE: Record<AttributeGroup, AttributeScope> = {
  customer: 'delivery',
  salesforce: 'delivery',
  answer: 'delivery',
  gap: 'delivery',
  deepdive: 'delivery',
  outcome: 'campaign',
  persona: 'campaign',
  role: 'campaign',
  capability: 'campaign',
  question: 'campaign',
  webex: 'campaign',
};

export interface AttributeDef {
  /**
   * The canonical exact key (`customer.name`) or, for a family, a human display template using
   * `<placeholder>` segments (`capability.<key>.name`). The template is shown in the picker header;
   * concrete instance keys are produced by the option layer and matched by `pattern`.
   */
  key: string;
  label: string;
  group: AttributeGroup;
  source: AttributeSource;
  help?: string;
  /** The equivalent legacy `[Bracketed]` spelling, shown as an author hint. Not used to resolve. */
  legacyAlias?: string;
  /**
   * Picker availability. `afterStepOrder` (a concrete instance's originating step order) hides the
   * option until the flow is past that step; assigned by the option layer for dynamic families, since
   * step order is campaign-specific and unknowable statically.
   */
  availability?: { afterStepOrder?: number; phase?: string };
  /** Present on family defs: matches concrete instance keys and captures the id segment(s). */
  pattern?: RegExp;
  /**
   * Resolve a concrete key against the context. `match` is the `pattern` result for families (undefined
   * for exact defs). Returning `undefined` leaves the token verbatim — the "no blank leak" contract.
   */
  resolve: (ctx: AttributeContext, match?: RegExpMatchArray) => string | undefined;
}

/** Join a reference collection's display strings for a `*.list` token; `undefined` when empty. */
const joinList = (items: string[] | undefined): string | undefined =>
  items && items.length ? items.join(', ') : undefined;

/**
 * A stable, token-safe slug for a reference row that has no native `key` (outcomes, questions,
 * personas). Callers building both the picker options and the resolution context MUST use this so the
 * concrete key the picker inserts matches the key the resolver looks up. Lowercases and collapses to
 * the `[a-z0-9_]` charset; falls back to the numeric id when a label slugs to nothing.
 */
export const refKey = (label: string | undefined, id: number | undefined): string => {
  const slug = (label ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return slug || `id_${id ?? 0}`;
};

export const CUSTOMER_ATTRIBUTES: AttributeDef[] = [
  {
    key: 'customer.name',
    label: 'Customer name',
    group: 'customer',
    source: 'static',
    help: "This delivery's account name.",
    legacyAlias: '[Customer Name]',
    resolve: (ctx) => ctx.customer?.name,
  },
  {
    key: 'customer.opportunity',
    label: 'Opportunity',
    group: 'customer',
    source: 'static',
    help: 'The linked Salesforce opportunity name.',
    legacyAlias: '[Opportunity Name]',
    resolve: (ctx) => ctx.customer?.opportunity,
  },
  {
    key: 'customer.account_id',
    label: 'Account ID',
    group: 'customer',
    source: 'static',
    help: "The account's CCO / Salesforce id.",
    legacyAlias: '[Account ID]',
    resolve: (ctx) => ctx.customer?.accountId,
  },
  {
    key: 'customer.se_name',
    label: 'SE name',
    group: 'customer',
    source: 'static',
    help: 'The SE running this delivery.',
    legacyAlias: '[SE Name]',
    resolve: (ctx) => ctx.customer?.seName,
  },
  {
    key: 'customer.date',
    label: 'Date',
    group: 'customer',
    source: 'static',
    help: "Today's date when the text is generated.",
    legacyAlias: '[Date]',
    resolve: (ctx) => ctx.customer?.date,
  },
];

/** Salesforce field families — `sf.account.<field>` / `sf.opportunity.<field>` / `sf.contact.<field>`. */
const sfFamily = (
  bag: 'account' | 'opportunity' | 'contact',
  label: string,
): AttributeDef => ({
  key: `sf.${bag}.<field>`,
  label: `${label} field`,
  group: 'salesforce',
  source: 'sf',
  help: `A field from the linked Salesforce ${bag}.`,
  pattern: new RegExp(`^sf\\.${bag}\\.([a-z0-9_]+)$`),
  resolve: (ctx, match) => (match ? ctx.sf?.[bag]?.[match[1]] : undefined),
});

export const SALESFORCE_ATTRIBUTES: AttributeDef[] = [
  sfFamily('account', 'Salesforce account'),
  sfFamily('opportunity', 'Salesforce opportunity'),
  sfFamily('contact', 'Salesforce contact'),
];

export const REFERENCE_ATTRIBUTES: AttributeDef[] = [
  {
    key: 'outcome.list',
    label: 'All outcomes',
    group: 'outcome',
    source: 'reference',
    help: 'The campaign\'s intended outcomes, comma-joined by priority.',
    resolve: (ctx) => joinList(ctx.references?.outcomes?.map((o) => o.title)),
  },
  {
    key: 'outcome.<key>.title',
    label: 'Outcome title',
    group: 'outcome',
    source: 'reference',
    help: 'A specific outcome\'s title.',
    pattern: /^outcome\.([a-z0-9_]+)\.title$/,
    resolve: (ctx, match) =>
      match ? ctx.references?.outcomes?.find((o) => o.key === match[1])?.title : undefined,
  },
  {
    key: 'persona.list',
    label: 'All personas',
    group: 'persona',
    source: 'reference',
    help: 'The customer-side personas to get in the room, comma-joined.',
    resolve: (ctx) => joinList(ctx.references?.personas?.map((p) => p.label)),
  },
  {
    key: 'persona.<key>.label',
    label: 'Persona label',
    group: 'persona',
    source: 'reference',
    help: 'A specific persona\'s label.',
    pattern: /^persona\.([a-z0-9_]+)\.label$/,
    resolve: (ctx, match) =>
      match ? ctx.references?.personas?.find((p) => p.key === match[1])?.label : undefined,
  },
  {
    key: 'role.list',
    label: 'All team roles',
    group: 'role',
    source: 'reference',
    help: 'The Cisco-side team roles, comma-joined.',
    resolve: (ctx) => joinList(ctx.references?.roles?.map((r) => r.name)),
  },
  {
    key: 'role.<key>.name',
    label: 'Team role name',
    group: 'role',
    source: 'reference',
    help: 'A specific team role\'s name.',
    pattern: /^role\.([a-z0-9_]+)\.name$/,
    resolve: (ctx, match) =>
      match ? ctx.references?.roles?.find((r) => r.key === match[1])?.name : undefined,
  },
  {
    key: 'capability.list',
    label: 'All capabilities',
    group: 'capability',
    source: 'reference',
    help: 'The campaign\'s capabilities/plays, comma-joined.',
    resolve: (ctx) => joinList(ctx.references?.capabilities?.map((c) => c.name)),
  },
  {
    key: 'capability.<key>.name',
    label: 'Capability name',
    group: 'capability',
    source: 'reference',
    help: 'A specific capability\'s name.',
    pattern: /^capability\.([a-z0-9_]+)\.name$/,
    resolve: (ctx, match) =>
      match ? ctx.references?.capabilities?.find((c) => c.key === match[1])?.name : undefined,
  },
  {
    key: 'question.list',
    label: 'All pool questions',
    group: 'question',
    source: 'reference',
    help: 'The campaign\'s question pool, comma-joined.',
    resolve: (ctx) => joinList(ctx.references?.questions?.map((q) => q.text)),
  },
  {
    key: 'question.<key>.text',
    label: 'Question text',
    group: 'question',
    source: 'reference',
    help: 'A specific pool question\'s text.',
    pattern: /^question\.([a-z0-9_]+)\.text$/,
    resolve: (ctx, match) =>
      match ? ctx.references?.questions?.find((q) => q.key === match[1])?.text : undefined,
  },
];

export const DYNAMIC_ATTRIBUTES: AttributeDef[] = [
  {
    key: 'answer.<stepKey>.<questionKey>',
    label: 'Captured answer',
    group: 'answer',
    source: 'dynamic',
    help: "A customer's captured answer to a discovery/deep-dive question. Verbatim until captured.",
    pattern: /^answer\.([a-z0-9_]+)\.([a-z0-9_]+)$/,
    resolve: (ctx, match) =>
      match ? ctx.captured?.answers?.[`${match[1]}.${match[2]}`] : undefined,
  },
  {
    key: 'gap.open.list',
    label: 'Open gaps',
    group: 'gap',
    source: 'dynamic',
    help: 'The open gaps identified in the deep dive, comma-joined. Verbatim until identified.',
    resolve: (ctx) => joinList(ctx.captured?.openGaps),
  },
  {
    key: 'deepdive.findings',
    label: 'Deep-dive findings',
    group: 'deepdive',
    source: 'dynamic',
    help: 'The assembled deep-dive findings prose. Verbatim until the deep dive runs.',
    resolve: (ctx) => {
      const v = ctx.captured?.deepDiveFindings;
      return v && v.trim() ? v : undefined;
    },
  },
];

export const WEBEX_ATTRIBUTES: AttributeDef[] = [
  {
    key: 'webex.people',
    label: 'People to schedule',
    group: 'webex',
    source: 'reference',
    help: 'The workshop roster (selected team roles + personas) formatted for scheduling.',
    resolve: (ctx) => {
      const v = ctx.webex?.people;
      return v && v.trim() ? v : undefined;
    },
  },
];

/** The full catalog, in picker display order. */
export const ATTRIBUTE_CATALOG: AttributeDef[] = [
  ...CUSTOMER_ATTRIBUTES,
  ...SALESFORCE_ATTRIBUTES,
  ...REFERENCE_ATTRIBUTES,
  ...DYNAMIC_ATTRIBUTES,
  ...WEBEX_ATTRIBUTES,
];

/** Human-facing labels for each attribute group (picker section headers). */
export const ATTRIBUTE_GROUP_LABELS: Record<AttributeGroup, string> = {
  customer: 'Customer basics',
  salesforce: 'Salesforce fields',
  outcome: 'Outcomes',
  persona: 'Personas',
  role: 'Team roles',
  capability: 'Capabilities',
  question: 'Question pool',
  answer: 'Captured answers',
  gap: 'Gaps',
  deepdive: 'Deep dive',
  webex: 'Scheduling',
};

/**
 * Find the catalog def that owns a concrete key: an exact-key match first, then a family whose
 * `pattern` matches. Returns the def and the pattern match (for family resolution), or `undefined` when
 * no def owns the key (⇒ the resolver leaves it verbatim).
 */
export function lookupAttribute(
  key: string,
): { def: AttributeDef; match?: RegExpMatchArray } | undefined {
  for (const def of ATTRIBUTE_CATALOG) {
    if (!def.pattern && def.key === key) return { def };
  }
  for (const def of ATTRIBUTE_CATALOG) {
    if (def.pattern) {
      const match = key.match(def.pattern);
      if (match) return { def, match };
    }
  }
  return undefined;
}
